import { ReactElement, useState } from 'react'
import { buildErrorMessage } from '@constants'
import { Box, Button, Stack, Typography } from '@mui/material'
import type { InterruptRenderProps } from '@copilotkit/react-core/v2'
import { useSnackbar } from '@ui/snackbar'
import {
  NO_FILES_REASON,
  QuestionAnswer,
  choiceCardOf,
  fileRequestCardOf,
  importPlanCardOf,
  projectChangeCardOf,
  questionCardOf,
} from '../utils'
import ChangeApprovalItem from './ChangeApprovalItem'
import ChoiceItem from './ChoiceItem'
import { ChangeDecision } from './DecisionButtons'
import FloorplanFileRequestItem from './FloorplanFileRequestItem'
import IfcFileRequestItem from './IfcFileRequestItem'
import ImportPlanItem from './ImportPlanItem'
import QuestionItem from './QuestionItem'

type Props = Pick<InterruptRenderProps, 'interrupts' | 'resolve'>

/**
 * What the agent paused for, rendered into the chat: questions with options
 * it asks, files it asks for (uploaded right here), its import plan and the
 * changes it wants to make. Each is approved or denied on its own (or all
 * at once); once every change is
 * decided, CopilotKit resumes the agent with the decisions. Only the API
 * turns them into permission to write.
 */
const ChangeApprovalCard = ({ interrupts, resolve }: Props): ReactElement | null => {
  const { enqueueSnackbar } = useSnackbar()
  const [decisions, setDecisions] = useState<Record<string, ChangeDecision>>({})
  // A question's answer once sent (null: dismissed), shown on its card.
  const [answers, setAnswers] = useState<Record<string, QuestionAnswer | null>>({})
  const plans = interrupts.flatMap(interrupt => {
    const plan = importPlanCardOf(interrupt)
    return plan ? [{ id: interrupt.id, plan }] : []
  })
  const changes = interrupts.flatMap(interrupt => {
    const card = projectChangeCardOf(interrupt)
    return card ? [{ id: interrupt.id, card }] : []
  })
  const fileRequests = interrupts.flatMap(interrupt => {
    const card = fileRequestCardOf(interrupt)
    return card ? [{ id: interrupt.id, card }] : []
  })
  const choices = interrupts.flatMap(interrupt => {
    const choice = choiceCardOf(interrupt)
    return choice ? [{ id: interrupt.id, choice }] : []
  })
  const questions = interrupts.flatMap(interrupt => {
    const question = questionCardOf(interrupt)
    return question ? [{ id: interrupt.id, question }] : []
  })
  if (
    changes.length === 0 &&
    plans.length === 0 &&
    fileRequests.length === 0 &&
    choices.length === 0 &&
    questions.length === 0
  ) {
    return null
  }
  const open = [...plans, ...changes].filter(item => !decisions[item.id])

  const decide = async (ids: string[], approved: boolean, reason?: string) => {
    const decision: ChangeDecision = approved ? 'approved' : 'denied'
    setDecisions(previous => ({
      ...previous,
      ...Object.fromEntries(ids.map(id => [id, decision])),
    }))
    let sent = 0
    try {
      for (const id of ids) {
        await resolve(approved ? { approved } : { approved, reason }, id)
        sent += 1
      }
    } catch (error) {
      // The agent never got these: offer them for a decision again.
      const unsent = new Set(ids.slice(sent))
      setDecisions(previous =>
        Object.fromEntries(Object.entries(previous).filter(([id]) => !unsent.has(id))),
      )
      enqueueSnackbar(
        buildErrorMessage(error as Error, 'KI-Chat: Entscheidung konnte nicht gesendet werden'),
        { variant: 'error' },
      )
    }
  }

  // The API checks the chosen ids against the question's options; a dismissed
  // question reaches the agent as "not answered".
  const answer = async (id: string, given: QuestionAnswer | null) => {
    setAnswers(previous => ({ ...previous, [id]: given }))
    try {
      await resolve(given ? { approved: true, answer: given } : { approved: false }, id)
    } catch (error) {
      setAnswers(previous => Object.fromEntries(Object.entries(previous).filter(([k]) => k !== id)))
      enqueueSnackbar(
        buildErrorMessage(error as Error, 'KI-Chat: Antwort konnte nicht gesendet werden'),
        { variant: 'error' },
      )
    }
  }

  const onlyQuestions = plans.length === 0 && changes.length === 0 && fileRequests.length === 0

  return (
    <Box
      sx={{ border: 1, borderColor: 'warning.main', borderRadius: 1, p: 1, my: 1 }}
      data-cy="ai-chat-change-approval"
    >
      <Typography variant="subtitle2" mb={1}>
        {questions.length > 0 && choices.length === 0 && onlyQuestions
          ? 'Der Assistent hat eine Frage'
          : choices.length > 0 && plans.length === 0 && changes.length === 0
          ? 'Der Assistent braucht eine Entscheidung'
          : fileRequests.length > 0 && plans.length === 0 && changes.length === 0
          ? 'Der Assistent benötigt Dateien'
          : plans.length > 0
          ? 'Der Assistent bittet um Freigabe seines Importplans'
          : changes.length === 1
          ? 'Der Assistent möchte das Projekt ändern'
          : `Der Assistent möchte ${changes.length} Änderungen am Projekt vornehmen`}
      </Typography>
      <Stack spacing={1}>
        {questions.map(({ id, question }) => (
          <QuestionItem
            key={id}
            question={question}
            answer={answers[id]}
            onAnswer={given => answer(id, given)}
            onDismiss={() => answer(id, null)}
          />
        ))}
        {choices.map(({ id, choice }) => (
          <ChoiceItem
            key={id}
            choice={choice}
            decision={decisions[id]}
            onDecide={approved => decide([id], approved, approved ? undefined : choice.denyReason)}
          />
        ))}
        {fileRequests.map(({ id, card }) => {
          const onDecide = (uploaded: boolean) =>
            decide([id], uploaded, uploaded ? undefined : NO_FILES_REASON)
          return card.fileKind === 'ifc' ? (
            <IfcFileRequestItem
              key={id}
              message={card.message}
              decision={decisions[id]}
              onDecide={onDecide}
            />
          ) : (
            <FloorplanFileRequestItem
              key={id}
              card={card}
              decision={decisions[id]}
              onDecide={onDecide}
            />
          )
        })}
        {plans.map(item => (
          <ImportPlanItem
            key={item.id}
            plan={item.plan}
            decision={decisions[item.id]}
            onDecide={approved => decide([item.id], approved)}
          />
        ))}
        {changes.map(change => (
          <ChangeApprovalItem
            key={change.id}
            card={change.card}
            decision={decisions[change.id]}
            onDecide={approved => decide([change.id], approved)}
          />
        ))}
      </Stack>
      {open.length > 1 && (
        <Stack direction="row" spacing={1} mt={1} justifyContent="flex-end">
          <Button
            size="small"
            color="inherit"
            onClick={() =>
              decide(
                open.map(change => change.id),
                false,
              )
            }
            data-cy="ai-chat-change-deny-all"
          >
            Alle ablehnen
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() =>
              decide(
                open.map(change => change.id),
                true,
              )
            }
            data-cy="ai-chat-change-approve-all"
          >
            Alle freigeben
          </Button>
        </Stack>
      )}
    </Box>
  )
}

export default ChangeApprovalCard
